import useAuthStore from '@/stores/useAuthStore';
import { getEnv } from '@/utils/envUtils';
import {
    HttpTransportType,
    HubConnection,
    HubConnectionBuilder,
    HubConnectionState,
    LogLevel,
} from '@microsoft/signalr';
import { type Ref, ref } from 'vue';

const { VITE_API_BASE } = getEnv();

const MAX_RECONNECT_RETRIES = 5;
const RECONNECT_BASE_DELAY_MS = 1000; // Base delay for reconnection attempts (1 second)
const RECONNECT_MAX_DELAY_MS = 30000; // Maximum delay for reconnection attempts (30 seconds)
const AUTHENTICATION_ERROR = 'SignalR authentication unavailable';

let reconnectRetryLimitReached = false;

const connection: Ref<HubConnection | null> = ref(null);
let startupPromise: Promise<boolean> | null = null;
let startupGeneration = 0;
let stopPromise: Promise<void> | null = null;
let generation = 0;
let intentionallyStopped = false;
let cancelRetry: (() => void) | null = null;

const retryDelay = (retryCount: number) =>
    Math.min(RECONNECT_BASE_DELAY_MS * 2 ** retryCount, RECONNECT_MAX_DELAY_MS);

const waitForRetry = (retryCount: number) =>
    new Promise<void>(resolve => {
        const timer = setTimeout(() => {
            cancelRetry = null;
            resolve();
        }, retryDelay(retryCount));
        cancelRetry = () => {
            clearTimeout(timer);
            cancelRetry = null;
            resolve();
        };
    });

const isConnected = (conn: HubConnection) => conn.state === HubConnectionState.Connected;

const getHubUrl = () => {
    try {
        if (!VITE_API_BASE?.trim()) {
            throw new Error('Missing SignalR API URL');
        }
        const url = new URL(VITE_API_BASE.trim(), globalThis.window.location.origin);
        if (
            !['http:', 'https:', 'ws:', 'wss:'].includes(url.protocol) ||
            [url.username, url.password, url.search, url.hash].some(Boolean)
        ) {
            throw new Error('Unsupported SignalR API URL');
        }
        url.protocol =
            globalThis.window.location.protocol === 'https:' ||
            ['https:', 'wss:'].includes(url.protocol)
                ? 'https:'
                : 'http:';
        let basePath = url.pathname;
        while (basePath.endsWith('/')) {
            basePath = basePath.slice(0, -1);
        }
        url.pathname = `${basePath}/hubs/communication`;
        return url.href;
    } catch {
        throw new Error('Invalid SignalR API URL');
    }
};

export const getSignalRConnection = async () => {
    if (!useAuthStore().isAuthenticated) {
        return null;
    }
    if (!connection.value && useAuthStore().isAuthenticated) {
        connection.value = new HubConnectionBuilder()
            .withUrl(getHubUrl(), {
                accessTokenFactory: async () => {
                    const auth = useAuthStore();
                    const tokenGeneration = generation;
                    if (!auth.isAuthenticated || intentionallyStopped) {
                        throw new Error(AUTHENTICATION_ERROR);
                    }
                    let token: string;
                    try {
                        token = await auth.getToken();
                    } catch {
                        throw new Error(AUTHENTICATION_ERROR);
                    }
                    if (
                        !token?.trim() ||
                        !auth.isAuthenticated ||
                        intentionallyStopped ||
                        tokenGeneration !== generation
                    ) {
                        throw new Error(AUTHENTICATION_ERROR);
                    }
                    return token;
                },
                withCredentials: true,
                skipNegotiation: true,
                transport: HttpTransportType.WebSockets,
            })
            .withAutomaticReconnect({
                nextRetryDelayInMilliseconds: retryContext => {
                    if (intentionallyStopped || !useAuthStore().isAuthenticated) {
                        return null;
                    }
                    if (retryContext.previousRetryCount >= MAX_RECONNECT_RETRIES) {
                        reconnectRetryLimitReached = true;
                        return null; // Stop retrying after max retries
                    }
                    return retryDelay(retryContext.previousRetryCount);
                },
            })
            .configureLogging(LogLevel.None)
            .build();
    }
    return connection.value;
};

export const hasSignalRReconnectRetryLimitReached = () => {
    return reconnectRetryLimitReached;
};

export const resetSignalRReconnectRetryLimitReached = () => {
    reconnectRetryLimitReached = false;
};

export const stopSignalR = async () => {
    intentionallyStopped = true;
    generation += 1;
    cancelRetry?.();
    reconnectRetryLimitReached = false;
    stopPromise ??= (async () => {
        await connection.value?.stop();
    })();
    try {
        await stopPromise;
    } finally {
        stopPromise = null;
    }
};

export const startSignalR = async (): Promise<boolean> => {
    const currentGeneration = generation;
    if (startupPromise) {
        if (startupGeneration === currentGeneration) {
            return startupPromise;
        }
        await startupPromise;
        return currentGeneration === generation && useAuthStore().isAuthenticated
            ? startSignalR()
            : false;
    }
    startupGeneration = currentGeneration;
    const isActive = () => currentGeneration === generation && useAuthStore().isAuthenticated;
    startupPromise = (async () => {
        await stopPromise;
        if (!isActive()) {
            return false;
        }
        intentionallyStopped = false;
        const conn = await getSignalRConnection();
        if (!conn || !isActive()) {
            return false;
        }
        reconnectRetryLimitReached = false;
        const attemptStart = async (retryCount: number): Promise<boolean> => {
            if (!isActive()) {
                return false;
            }
            if (conn.state !== HubConnectionState.Disconnected) {
                return isConnected(conn);
            }
            try {
                await conn.start();
                return isActive() && isConnected(conn);
            } catch {
                if (!isActive()) {
                    return false;
                }
                if (retryCount >= MAX_RECONNECT_RETRIES) {
                    reconnectRetryLimitReached = true;
                    throw new Error('SignalR connection failed');
                }
                await waitForRetry(retryCount);
                return attemptStart(retryCount + 1);
            }
        };
        return attemptStart(0);
    })();
    try {
        return await startupPromise;
    } finally {
        startupPromise = null;
    }
};
